import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import { useTranslation } from 'react-i18next';
import { BorrowerIcon } from '../BorrowerIcon';
import styles from '../styles.module.scss';
import {
  Borrower,
  BorrowerExtends,
  CMHCLowRatioCharacteristicsSectionProps,
  KeyLoanCharacteristicsSectionProps,
  OtherLoanCharacteristicsSectionProps,
  PropertyCharacteristicsSectionProps,
  RequestDetailsSectionProps
} from '../types';
import { CMHCLowRatioCharacteristicsSection } from './cmhcLowRatioCharacteristicsSection';
import { KeyLoanCharacteristicsSection } from './keyLoanCharacteristicsSection';
import { OtherLoanCharacteristicsSection } from './otherLoanCharacteristicsSection';
import { PropertyCharacteristicsSection } from './propertyCharacteristicsSection';
import { RequestDetailsSection } from './requestDetailsSection';
import CopyContentToClipBoard from './CopyContentToClipBoard';
import { ToastNotification, ToastNotificationVariant } from '@cmhc-schl/hearth-ui';
import { useLocation } from 'react-router-dom';
import { getSearchParams } from 'Feature/V2/Shared_Portfolio/components/SearchFlyout/utils/getSearchParams';
import { handleScrollTo } from '../index';

interface LayoutContainerProp {
  borrowerDetails?: Borrower[] | BorrowerExtends[];
  requestDetailsSectionPropDetails?: RequestDetailsSectionProps;
  propertyCharacteristicsSectionPropDetails?: PropertyCharacteristicsSectionProps;
  otherLoanCharacteristicsSectionPropDetails?: OtherLoanCharacteristicsSectionProps;
  keyLoanCharacteristicsSectionPropDetails?: KeyLoanCharacteristicsSectionProps;
  cmhcLowRatioCharacteristicsSectionPropDetails?: CMHCLowRatioCharacteristicsSectionProps;
  options: {
    LayoutContainerType: 'masterpool' | 'validation review';
    isReducedDataSet?: boolean;
  };
}

export const LayoutContainer = ({
  borrowerDetails,
  requestDetailsSectionPropDetails,
  propertyCharacteristicsSectionPropDetails,
  otherLoanCharacteristicsSectionPropDetails,
  keyLoanCharacteristicsSectionPropDetails,
  cmhcLowRatioCharacteristicsSectionPropDetails,
  options
}: LayoutContainerProp) => {
  const { t } = useTranslation('HbtPortfolioManagementRequestOverview');
  const [showToaster, setShowToaster] = useState(false);
  const [toaster, setToasterMsg] = useState({
    icon: 'icon_check_circle',
    message: {
      header: t('Copied'),
      subHeader: String(' ')
    }
  });
  const location = useLocation();
  const { dataType } = getSearchParams(location.search);

  const isBorrowerExtends = (data: Borrower | BorrowerExtends): data is BorrowerExtends => {
    return (data as BorrowerExtends).CMHCCreditScore !== undefined;
  };

  const sectionRefs = {
    borrower: useRef(null),
    propertyCharacteristics: useRef(null)
  };

  useEffect(() => {
    if (!sessionStorage.getItem('searchLoanDetail')) return;
    const shouldScrollToBorrowerSection =
      borrowerDetails && sectionRefs.borrower.current && dataType === 'Borrower';
    const shouldScrollToPropertySection =
      propertyCharacteristicsSectionPropDetails &&
      sectionRefs.propertyCharacteristics.current &&
      dataType === 'Property Address';
    if (shouldScrollToBorrowerSection) {
      handleScrollTo(sectionRefs.borrower);
    } else if (shouldScrollToPropertySection) {
      handleScrollTo(sectionRefs.propertyCharacteristics);
    }
  }, [
    borrowerDetails,
    propertyCharacteristicsSectionPropDetails,
    cmhcLowRatioCharacteristicsSectionPropDetails,
    sectionRefs.borrower.current,
    sectionRefs.propertyCharacteristics.current
  ]);

  return (
    <>
      <Box className={styles.toasterNotification}>
        <ToastNotification
          dismissMode="autoAndActionable"
          variant={ToastNotificationVariant.INFO}
          open={showToaster}
          header={{ value: toaster.message.header }}
          subHeader={{ value: toaster.message.subHeader }}
          onClose={() => setShowToaster(false)}
          icon={toaster.icon}
        />
      </Box>

      <Box className={styles.layoutContainer}>
        <Box className={styles.borrowersContainer}>
          <Box className={styles.headerColumn}>
            <Box className={styles.titleRow}>
              <Typography variant="h6" className={styles.labelBorrowers}>
                {borrowerDetails && borrowerDetails.length === 1
                  ? t('PI-Portfolio-Management-Request-Overview-Borrower')
                  : t('PI-Portfolio-Management-Request-Overview-Borrowers')}
              </Typography>
              {borrowerDetails && borrowerDetails.length > 1 && (
                <Typography variant="h6" className={styles.labelBorrowers}>
                  1-2
                </Typography>
              )}
            </Box>
            <Box className={styles.borrowerRow}>
              <Typography variant="h6" className={styles.rowText}>
                {t('Date-of-Birth')}
              </Typography>
            </Box>
            <Box className={styles.borrowerRow}>
              <Typography variant="h6" className={styles.rowText}>
                {t('Address')}
              </Typography>
            </Box>
            {options.LayoutContainerType === 'validation review' && (
              <Box className={styles.borrowerRow}>
                <Typography variant="h6" className={styles.rowText}>
                  {t('Credit-Score')}
                </Typography>
              </Box>
            )}
            {options.LayoutContainerType === 'masterpool' && (
              <>
                <Box className={styles.borrowerRow}>
                  <Typography variant="h6" className={styles.rowText}>
                    {t('Lender-Credit-Score')}
                  </Typography>
                </Box>
                <Box className={styles.borrowerRow}>
                  <Typography variant="h6" className={styles.rowText}>
                    {t('CMHC-Credit-Score')}
                  </Typography>
                </Box>
              </>
            )}
            <Box className={styles.borrowerRow}>
              <Typography variant="h6" className={styles.rowText}>
                {t('Total-Income')}
              </Typography>
            </Box>
          </Box>
          {borrowerDetails &&
            borrowerDetails.map((borrower: Borrower, index: number) => (
              <Box key={index} className={styles.borrowerColumn} ref={sectionRefs.borrower}>
                <Box className={styles.titleRow}>
                  <Box className={styles.BorrowerIcon}>
                    <BorrowerIcon index={index} />
                  </Box>
                  <Typography variant="h6" className={styles.BorrowerName}>
                    <MemoizedHbtDataTableCell
                      dataValue={String(borrower.name)}
                      cellContent={<span>{borrower.name}</span>}
                    />
                  </Typography>
                </Box>
                <Box className={styles.borrowerRow}>
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(borrower.dateOfBirth)}
                    clipboardContent={borrower.dateOfBirth}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(borrower.dateOfBirth)}
                      cellContent={
                        <Typography variant="h6" className={styles.rowText}>
                          {borrower.dateOfBirth}
                        </Typography>
                      }
                    />
                  </CopyContentToClipBoard>
                </Box>
                <Box className={styles.borrowerRow}>
                  <Typography variant="h6" className={styles.rowText}>
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(borrower.address)}
                      clipboardContent={borrower.address}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(borrower.address)}
                        cellContent={
                          <Typography variant="h6" className={styles.rowText}>
                            {borrower.address}
                          </Typography>
                        }
                      />
                    </CopyContentToClipBoard>
                  </Typography>
                </Box>
                {options.LayoutContainerType === 'validation review' && (
                  <Box className={styles.borrowerRow}>
                    <CopyContentToClipBoard
                      onSuccess={() => setShowToaster(true)}
                      showClipButton={Boolean(borrower.creditScore)}
                      clipboardContent={borrower.creditScore}
                    >
                      <MemoizedHbtDataTableCell
                        dataValue={String(borrower.creditScore)}
                        cellContent={
                          <Typography variant="h6" className={styles.rowText}>
                            {borrower.creditScore}
                          </Typography>
                        }
                      />
                    </CopyContentToClipBoard>
                  </Box>
                )}
                {options.LayoutContainerType === 'masterpool' && isBorrowerExtends(borrower) && (
                  <>
                    <Box className={styles.borrowerRow}>
                      <CopyContentToClipBoard
                        onSuccess={() => setShowToaster(true)}
                        showClipButton={Boolean(borrower.lenderCreditScore)}
                        clipboardContent={borrower.lenderCreditScore}
                      >
                        <MemoizedHbtDataTableCell
                          dataValue={String(borrower.lenderCreditScore)}
                          cellContent={
                            <Typography variant="h6" className={styles.rowText}>
                              {borrower.lenderCreditScore}
                            </Typography>
                          }
                        />
                      </CopyContentToClipBoard>
                    </Box>
                    <Box className={styles.borrowerRow}>
                      <CopyContentToClipBoard
                        onSuccess={() => setShowToaster(true)}
                        showClipButton={Boolean(borrower.CMHCCreditScore)}
                        clipboardContent={borrower.CMHCCreditScore}
                      >
                        <MemoizedHbtDataTableCell
                          dataValue={String(borrower.CMHCCreditScore)}
                          cellContent={
                            <Typography variant="h6" className={styles.rowText}>
                              {borrower.CMHCCreditScore}
                            </Typography>
                          }
                        />
                      </CopyContentToClipBoard>
                    </Box>
                  </>
                )}
                <Box className={styles.borrowerRow}>
                  <CopyContentToClipBoard
                    onSuccess={() => setShowToaster(true)}
                    showClipButton={Boolean(borrower.totalIncome)}
                    clipboardContent={borrower.totalIncome}
                  >
                    <MemoizedHbtDataTableCell
                      dataValue={String(borrower.totalIncome)}
                      cellContent={
                        <Typography variant="h6" className={styles.rowText}>
                          {borrower.totalIncome}
                        </Typography>
                      }
                    />
                  </CopyContentToClipBoard>
                </Box>
              </Box>
            ))}
        </Box>
        {requestDetailsSectionPropDetails && (
          <RequestDetailsSection
            isCollapsed={requestDetailsSectionPropDetails.isCollapsed}
            onToggle={requestDetailsSectionPropDetails.onToggle}
            lenderDetails={requestDetailsSectionPropDetails.lenderDetails}
          />
        )}
        {propertyCharacteristicsSectionPropDetails && (
          <Box
            className={propertyCharacteristicsSectionPropDetails.className}
            ref={sectionRefs.propertyCharacteristics}
          >
            <PropertyCharacteristicsSection
              className={propertyCharacteristicsSectionPropDetails.className}
              isCollapsed={propertyCharacteristicsSectionPropDetails.isCollapsed}
              onToggle={propertyCharacteristicsSectionPropDetails.onToggle}
              propertyData={propertyCharacteristicsSectionPropDetails.propertyData}
            />
          </Box>
        )}
        {keyLoanCharacteristicsSectionPropDetails && (
          <KeyLoanCharacteristicsSection
            isCollapsed={keyLoanCharacteristicsSectionPropDetails.isCollapsed}
            onToggle={keyLoanCharacteristicsSectionPropDetails.onToggle}
            keyLoanData={keyLoanCharacteristicsSectionPropDetails.keyLoanData}
            options={keyLoanCharacteristicsSectionPropDetails.options}
          />
        )}
        {cmhcLowRatioCharacteristicsSectionPropDetails &&
          cmhcLowRatioCharacteristicsSectionPropDetails.cmhcLowRatioData
            ?.lowRatioCmhcAccountNumber && (
            <CMHCLowRatioCharacteristicsSection
              isCollapsed={cmhcLowRatioCharacteristicsSectionPropDetails.isCollapsed}
              onToggle={cmhcLowRatioCharacteristicsSectionPropDetails.onToggle}
              cmhcLowRatioData={cmhcLowRatioCharacteristicsSectionPropDetails.cmhcLowRatioData}
              options={cmhcLowRatioCharacteristicsSectionPropDetails.options}
            />
          )}
        {otherLoanCharacteristicsSectionPropDetails && (
          <OtherLoanCharacteristicsSection
            isCollapsed={otherLoanCharacteristicsSectionPropDetails.isCollapsed}
            onToggle={otherLoanCharacteristicsSectionPropDetails.onToggle}
            otherLoanData={otherLoanCharacteristicsSectionPropDetails.otherLoanData}
          />
        )}
      </Box>
    </>
  );
};
